import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import GenericDraggableTableView from "../../../../Features/Table/GenericDraggableTable";
import { getColumnsConfig } from "../../../../Features/Table/GenericDraggableTable/GenericDraggableTableFuncations";
import { MAX_COLUMN_SELECTION_COUNT } from "../../../../Helper/DigitalTwin/DataFile";
import { usePagesContent } from "../../../../Hooks/usePagesContent";
import { fetchFiltersDataObject } from "../../../../Helper/Table/TableHelper";

const ChartDrillDownTable = ({
    tableConfigs,
    getTableData,
    applyTableAction,
    getCSVFileName,
    selectedDataPoint,
    pageSize = 20,
    customRenderers = {},
    isOpen = true,
    disablePagination = false,
    disableColumnSettings = true,
    filtersConfig = [],
}) => {
    const pagesContent = usePagesContent();

    const [tableData, setTableData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(pageSize);
    const [sortBy, setSortBy] = useState([]);

    // Filter state
    const [filters, setFilters] = useState({});
    const [selectedCustomFilters, setSelectedCustomFilters] = useState({});

    // Table column management
    const [initialColumns, setInitialColumns] = useState([]);
    const [tableColumns, setTableColumns] = useState([]);
    const [selectedColumnKeys, setSelectedColumnKeys] = useState([]);
    const [defaultSelectedColumnKeys, setDefaultSelectedColumnKeys] = useState([]);
    const [totalSelectedRowKeys, setTotalSelectedRowKeys] = useState(new Set());
    const [isSelectAll, setIsSelectAll] = useState(false);
    const [columnOrder, setColumnOrder] = useState([]);

    // Load filter options on mount (dynamic filters only)
    useEffect(() => {
        if (!filtersConfig || filtersConfig.length === 0) return;
        const initFilters = async () => {
            const filtersObj = await fetchFiltersDataObject(filtersConfig, {});
            setFilters(filtersObj);
        };
        initFilters();
    }, [filtersConfig]);

    // Reset state when component unmounts or isOpen changes
    useEffect(() => {
        if (!isOpen) {
            setTableData([]);
            setPageNumber(1);
            setRowsPerPage(pageSize);
            setTotalSelectedRowKeys(prev => prev.size === 0 ? prev : new Set());
            setIsSelectAll(false);
            setSortBy(prev => prev.length === 0 ? prev : []);
            setSelectedCustomFilters({});
        }
    }, [isOpen, pageSize]);

    // Reset pagination and sorting when selectedDataPoint changes
    useEffect(() => {
        if (isOpen && selectedDataPoint) {
            setPageNumber(prev => prev === 1 ? prev : 1);
            setSortBy(prev => prev.length === 0 ? prev : []);
            setTotalSelectedRowKeys(prev => prev.size === 0 ? prev : new Set());
            setIsSelectAll(false);
            setSelectedCustomFilters({});
        }
    }, [selectedDataPoint, isOpen]);

    // Setup table columns
    useEffect(() => {
        if (!tableConfigs?.tableColumnsConfig || !tableConfigs?.tableColumnOrder) {
            return;
        }

        const availableColumns = getColumnsConfig({
            tableColumns: tableConfigs.tableColumnsConfig,
            tableColumnOrder: tableConfigs.tableColumnOrder,
            customRenderers: customRenderers,
        });

        setInitialColumns(availableColumns);
        const defaultSelected = availableColumns.slice(0, MAX_COLUMN_SELECTION_COUNT);
        const defaultKeys = defaultSelected.map((col) => col.dataIndex);
        setTableColumns(defaultSelected);
        setSelectedColumnKeys(defaultKeys);
        setDefaultSelectedColumnKeys(defaultKeys);
        setColumnOrder(defaultSelected.map((col) => col.headerLabel));
    }, [tableConfigs, customRenderers]);

    // Fetch table data
    useEffect(() => {
        const fetchData = async () => {
            if (!isOpen || !getTableData) {
                return;
            }

            try {
                setIsLoading(true);
                const result = await getTableData(selectedDataPoint, pageNumber, rowsPerPage, sortBy, selectedCustomFilters);
                setTableData(result.data || []);
                setTotal(result.total || 0);
            } catch (error) {
                console.error("Error fetching table data:", error);
                setTableData([]);
                setTotal(0);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [isOpen, getTableData, pageNumber, rowsPerPage, selectedDataPoint, sortBy, selectedCustomFilters]);

    // Handle page change
    const handlePageChange = (page) => {
        setTotalSelectedRowKeys(new Set());
        setIsSelectAll(false);
        setPageNumber(page);
    };

    // Handle sort change
    const handleSortChange = (key, order) => {
        let columnKey = key;
        if (tableConfigs?.tableColumnsConfig?.[key]?.sortKey) {
            columnKey = tableConfigs?.tableColumnsConfig?.[key]?.sortKey;
        }

        const direction = order === "asc" ? "ASC" : "DESC";
        const newSortBy = [{ column: columnKey, direction }];
        setSortBy(newSortBy);
        setPageNumber(1);
        setTotalSelectedRowKeys(new Set());
        setIsSelectAll(false);
    };

    const getCurrentSorting = () => {
        if (sortBy && sortBy.length > 0) {
            const firstSort = sortBy[0];
            return {
                sortBy: firstSort.column,
                orderBy: firstSort.direction.toLowerCase()
            };
        }
        return {};
    };

    const onSelectChange = (record, selected) => {
        const updatedSelectedKeys = new Set(totalSelectedRowKeys);
        if (selected) {
            updatedSelectedKeys.add(record.id);
        } else {
            updatedSelectedKeys.delete(record.id);
            if (isSelectAll) {
                setIsSelectAll(false);
            }
        }
        setTotalSelectedRowKeys(updatedSelectedKeys);
    };

    const onSelectAllChange = (selected, selectedRows) => {
        setIsSelectAll(selected);
        setTotalSelectedRowKeys(
            selected ? new Set(selectedRows.map((row) => row?.id)) : new Set()
        );
    };

    const onColumnOrderChange = (newTableColumns) => {
        setColumnOrder(newTableColumns.map((col) => col.headerLabel));
    };

    const applySelectedColumns = (newSelectedKeys) => {
        const filteredColumns = initialColumns.filter((columnConfig) =>
            newSelectedKeys.includes(columnConfig.dataIndex)
        );

        setTableColumns(filteredColumns);
        setSelectedColumnKeys(newSelectedKeys);
        setColumnOrder(filteredColumns.map((col) => col.headerLabel));
    };

    const handleClearSelection = () => {
        setTotalSelectedRowKeys(new Set());
        setIsSelectAll(false);
    };

    // Wrapper function for CSV download
    const getCSVData = async () => {
        if (!applyTableAction) {
            return [];
        }
        const rowSelectionKeys = Array.from(totalSelectedRowKeys);
        const params = {
            columns: tableColumns,
            columnOrder,
            selectedFilters: selectedCustomFilters,
        };
        return await applyTableAction(rowSelectionKeys, "download", isSelectAll, params);
    };

    // Wrapper function for email
    const onSendEmail = async ({ sendTo, sendCc, subject, body }) => {
        if (!applyTableAction) {
            return;
        }
        const rowSelectionKeys = Array.from(totalSelectedRowKeys);
        const params = {
            columns: tableColumns,
            columnOrder,
            emailTo: sendTo,
            emailCc: sendCc,
            subject: subject,
            body: body,
            selectedFilters: selectedCustomFilters,
        };
        return await applyTableAction(rowSelectionKeys, "mail", isSelectAll, params);
    };

    const tableTitle = typeof tableConfigs?.title === "function"
        ? tableConfigs.title(selectedDataPoint)
        : tableConfigs?.title || "Table";

    const hasFilters = filtersConfig && filtersConfig.length > 0;

    return (
        <Box sx={{ flex: 1 }}>
            <GenericDraggableTableView
                title={tableTitle}
                showPrimaryButton={false}
                count={total || tableData.length}
                tableColumns={tableColumns}
                selectedColumns={selectedColumnKeys}
                tableData={tableData}
                availableEmailDomains={pagesContent?.metaInfo?.globalConfig?.["email.domains"] || []}
                initialColumns={initialColumns}
                applySelectedColumns={applySelectedColumns}
                defaultSelectedColumns={defaultSelectedColumnKeys}
                isLoading={isLoading}
                disablePagination={disablePagination}
                pageNumber={pageNumber}
                rowsPerPage={rowsPerPage}
                total={total !== undefined ? total : tableData.length}
                disableSearch={true}
                disableFiltersSettings={!hasFilters}
                filters={hasFilters ? filters : undefined}
                selectedFilters={hasFilters ? selectedCustomFilters : undefined}
                applyFilters={hasFilters ? (f) => {
                    setSelectedCustomFilters(f);
                    setPageNumber(1);
                    setTotalSelectedRowKeys(new Set());
                    setIsSelectAll(false);
                } : undefined}
                disableColumnSettings={disableColumnSettings}
                columnOrder={columnOrder}
                onColumnOrderChange={onColumnOrderChange}
                applySort={handleSortChange}
                initialSorting={getCurrentSorting()}
                emptyMessage={tableConfigs?.emptyMessage || "No data available"}
                containerStyles={{
                    backgroundColor: "#FFFFFF",
                    borderRadius: "8px",
                    border: "1px solid #E6E6E6",
                }}
                getTotalRowSelection={() => Array.from(totalSelectedRowKeys)}
                getTotalRowSelectionCount={() =>
                    isSelectAll ? (total || tableData.length) : totalSelectedRowKeys.size
                }
                rowSelection={{
                    selectedRowKeys: Array.from(totalSelectedRowKeys),
                    onSelect: onSelectChange,
                    onSelectAll: onSelectAllChange,
                }}
                onClearSelection={handleClearSelection}
                onPageChange={handlePageChange}
                getCSVData={applyTableAction ? getCSVData : undefined}
                getCsvColumnSettings={() => ({
                    columnOrder: columnOrder.filter(header => header !== 'Actions')
                })}
                getCSVFileName={getCSVFileName}
                onSendEmail={applyTableAction ? onSendEmail : undefined}
            />
        </Box>
    );
};

export default ChartDrillDownTable;